import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { sizes } from '@/common/styles/sizes';
import { XLineIcon } from '@/common/assets/icon/icon';
import { typography } from '@/common/styles/typographyTokens';


interface SideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  width?: string;
  showCloseButton?: boolean;
  /** 오른쪽 오프셋 (예: Agent 패널 너비) */
  rightOffset?: number;
}

const ModalSide: React.FC<SideModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = '500px',
  showCloseButton = true,
  rightOffset = 0
}) => {
  
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose} $rightOffset={rightOffset}>
      <ModalContent width={width} onClick={e => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          {showCloseButton && (
            <CloseButton onClick={onClose}>
              <XLineIcon size={24}/>
            </CloseButton>
          )}
        </Header>
        <Content>
          {children}
        </Content>
      </ModalContent>
    </Overlay>
  );
};

const MOBILE_BREAKPOINT = '768px';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const Overlay = styled.div<{ $rightOffset: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: ${props => props.$rightOffset}px;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-in-out;
  transition: right 0.3s ease;

  &.closing {
    animation: ${fadeOut} 0.3s ease-in-out;
  }

  /* 모바일: 우측 오프셋 무시하고 화면 전체를 덮는다 */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    right: 0;
    /* 작은 뷰포트에서 주소창 등으로 인한 100vh 이슈 방지 */
    height: 100dvh;
  }
`;

const ModalContent = styled.div<{ width: string }>`
  width: ${props => props.width};
  background-color: ${semanticColors.background.secondary};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-in-out;

  &.closing {
    animation: ${slideOut} 0.3s ease-in-out;
  }

  /* 모바일: 전체 화면 popup 으로 전환. 하단에서 위로 슬라이드 업. */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    box-shadow: none;
    animation: ${slideUp} 0.3s ease-in-out;

    &.closing {
      animation: ${slideDown} 0.3s ease-in-out;
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${sizes.padding[16]};
  border-bottom: 1px solid ${semanticColors.border.primary};

  /* 모바일: iOS 노치 영역까지 고려한 상단 패딩 */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${sizes.padding[12]} ${sizes.padding[16]};
    padding-top: max(${sizes.padding[12]}, env(safe-area-inset-top));
  }
`;

const Title = styled.span`
  ${typography.heading.h6};
  color: ${semanticColors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${sizes.padding[8]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* 모바일: iOS 홈바 영역까지 컨텐츠가 가려지지 않도록 */
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

export default ModalSide; 